import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Gauge, ClipboardCheck, HeartPulse, MessageSquare, BookOpen, ArrowRight, BarChart3, ShieldAlert, ArrowUp } from "lucide-react";
import { Button } from "../components/ui/button";
import HeroBackdrop from "../components/HeroBackdrop";
import { useContent } from "../context/ContentContext";

const ACRONYMS = [
  {
    id: "ipsga",
    acronym: "IPSGA",
    title: "The System of Motorcycle & Vehicle Control",
    icon: Gauge,
    accent: "#F59E0B",
    intro:
      "IPSGA is the heart of Roadcraft — a systematic way of approaching and negotiating any hazard. Each phase is considered in turn (but flexibly), keeping you safe, smooth and in full control. The Information phase runs right through every other phase.",
    items: [
      { k: "I", label: "Information", text: "Take, Use and Give information continuously (see T.U.G. below). Observation feeds every other phase and never stops." },
      { k: "P", label: "Position", text: "Position the machine or vehicle for safety, stability and the best view — the correct road position for the hazard ahead." },
      { k: "S", label: "Speed", text: "Adjust to a safe speed for the hazard using throttle, engine braking and brakes, arriving at the right speed before you need it." },
      { k: "G", label: "Gear", text: "Select the right gear for that speed so you have maximum control and the power to respond." },
      { k: "A", label: "Acceleration", text: "Accelerate smoothly and appropriately to leave the hazard safely once it is clear to do so." },
    ],
  },
  {
    id: "tug",
    acronym: "TUG",
    title: "The Information phase",
    icon: MessageSquare,
    accent: "#2563EB",
    intro:
      "T.U.G. breaks down the Information phase of IPSGA. Good information — taken early, used well and given clearly — is what makes advanced riding and driving safe.",
    items: [
      { k: "T", label: "Take", text: "Take information from the road, other road users, signs, road surface and the environment — scan near, middle and far distance." },
      { k: "U", label: "Use", text: "Use that information to plan: what can be seen, what can't be seen, and what may reasonably be expected to happen." },
      { k: "G", label: "Give", text: "Give information to others — signals, brake lights, position and, where helpful, the horn — so your intentions are clear." },
    ],
  },
  {
    id: "powdders",
    acronym: "POWDDERSS",
    title: "Pre-ride / pre-drive machine check",
    icon: ClipboardCheck,
    accent: "#059669",
    intro:
      "A quick, methodical check before every ride or drive helps catch problems before they catch you out. POWDDERSS is a popular memory aid — exact wording varies between groups, so always follow your tutor's guidance.",
    items: [
      { k: "P", label: "Petrol / Fuel", text: "Enough fuel for the planned journey (and reserve)." },
      { k: "O", label: "Oil", text: "Engine oil at the correct level; no leaks." },
      { k: "W", label: "Water", text: "Coolant level correct where applicable." },
      { k: "D", label: "Damage", text: "General condition — no damage that affects safety or roadworthiness." },
      { k: "D", label: "Drive", text: "Drive chain tension and lubrication (or belt/shaft condition)." },
      { k: "E", label: "Electrics", text: "Lights, indicators, brake light and horn all working." },
      { k: "R", label: "Rubber", text: "Tyres — tread, condition and correct pressures." },
      { k: "S", label: "Steering", text: "Steering moves freely through the full range; controls feel right." },
      { k: "S", label: "Suspension / Stands", text: "Suspension working correctly; stands retract properly and are in good condition." },
    ],
  },
  {
    id: "iamsafe",
    acronym: "IAMSAFE",
    title: "Am I fit to ride or drive?",
    icon: HeartPulse,
    accent: "#DC2626",
    intro:
      "The machine is only half of the equation — the rider or driver must be fit too. IAMSAFE is a simple self-assessment to run before setting off.",
    items: [
      { k: "I", label: "Illness", text: "Are you unwell in any way that could impair your riding or driving?" },
      { k: "A", label: "Attitude", text: "Is your mindset calm, patient and safety-focused — not rushed, distracted or aggressive?" },
      { k: "M", label: "Medication", text: "Are you taking anything that causes drowsiness or affects concentration?" },
      { k: "S", label: "Sleep", text: "Are you well rested? Too little sleep slows reactions and concentration long before you actually feel tired." },
      { k: "A", label: "Alcohol", text: "Any alcohol still in your system, including from the night before?" },
      { k: "F", label: "Food", text: "Have you eaten and had enough to drink? Low blood sugar and dehydration quietly reduce focus and stamina." },
      { k: "E", label: "Emotion", text: "Are you in the right emotional state to concentrate — not angry, upset or preoccupied by something else?" },
    ],
  },
];

const Bar = ({ label, value, color, suffix = "%" }) => (
  <div>
    <div className="flex justify-between text-sm mb-1">
      <span className="font-semibold text-[#0F2744]">{label}</span>
      <span className="font-bold" style={{ color }}>{value}{suffix}</span>
    </div>
    <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);

export default function Resources() {
  const { t } = useContent();
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div data-testid="resources-page">
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 left-6 z-50 inline-flex items-center gap-2 rounded-full bg-[#0F2744] text-white pl-4 pr-5 py-3 shadow-lg hover:bg-[#0B192C] transition-colors"
          data-testid="resources-go-top"
          aria-label="Back to the top options"
        >
          <ArrowUp className="h-4 w-4" /> <span className="text-sm font-semibold">Back to top</span>
        </button>
      )}
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0F2744] py-20 lg:py-24">
        <HeroBackdrop sectionKey="resources" image={t("img.resources", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/rdldmsn6_image.png")} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Helpful areas</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white max-w-3xl leading-tight">
            The tools & techniques behind advanced training
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl">
            Advanced riding and driving is built on a handful of simple, powerful systems. Here's a plain-English guide to the key ones your tutor will use — and the book they all come from.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {ACRONYMS.map((a) => (
              <a key={a.id} href={`#${a.id}`} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid={`jump-${a.id}`}>
                {a.acronym}
              </a>
            ))}
            <a href="#statistics" className="px-4 py-2 rounded-full text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="jump-statistics">Statistics</a>
            <a href="#roadcraft" className="px-4 py-2 rounded-full text-sm font-semibold bg-amber-500 text-[#0F2744] hover:bg-amber-600 transition-colors" data-testid="jump-roadcraft">Roadcraft</a>
          </div>
        </div>
      </section>

      {/* Acronym sections */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {ACRONYMS.map((a) => (
          <section key={a.id} id={a.id} className="scroll-mt-24" data-testid={`resource-${a.id}`}>
            <div className="flex items-start gap-4 mb-6">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: a.accent }}>
                <a.icon className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F2744] font-heading tracking-tight">{a.acronym}</h2>
                  <span className="text-slate-500 font-medium">{a.title}</span>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed">{t(`resources.${a.id}.intro`, a.intro)}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {a.items.map((it, i) => (
                <div key={i} className="flex items-start gap-4 rounded-xl bg-white border border-slate-200 p-4" data-testid={`${a.id}-item-${i}`}>
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center font-heading font-bold text-white shrink-0" style={{ background: a.accent }}>
                    {it.k}
                  </div>
                  <div>
                    <div className="font-bold text-[#0F2744]">{t(`resources.${a.id}.item${i}.label`, it.label)}</div>
                    <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{t(`resources.${a.id}.item${i}.text`, it.text)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Statistics */}
        <section id="statistics" className="scroll-mt-24" data-testid="resource-statistics">
          <div className="flex items-start gap-4 mb-6">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 bg-[#0F2744]">
              <BarChart3 className="h-7 w-7 text-white" />
            </div>
            <div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F2744] font-heading tracking-tight">Statistics</h2>
                <span className="text-slate-500 font-medium">Motorcycle safety in numbers</span>
              </div>
              <p className="mt-3 text-slate-600 leading-relaxed">{t("resources.stats.intro", "Advanced training isn't just about enjoyment — the numbers make a powerful safety case. Here's a plain-English summary of the latest Great Britain motorcycle casualty figures, and why post-test roadcraft matters so much.")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { n: "386", l: "Rider deaths in GB (2025)", s: "+14% on 2024", c: "#DC2626" },
              { n: "25%", l: "of all road deaths are riders", s: "~1% of traffic", c: "#F59E0B" },
              { n: "6,096", l: "riders killed or seriously injured", s: "in 2025", c: "#2563EB" },
              { n: "~50×", l: "higher fatality rate than cars", s: "per mile travelled", c: "#0F2744" },
            ].map((k, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`stat-card-${i}`}>
                <div className="text-3xl font-extrabold font-heading" style={{ color: k.c }}>{k.n}</div>
                <div className="mt-1 text-sm font-semibold text-[#0F2744] leading-snug">{k.l}</div>
                <div className="text-xs text-slate-400 mt-0.5">{k.s}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="stat-chart-share">
              <h3 className="font-bold text-[#0F2744] mb-1">A small share of traffic, a large share of harm</h3>
              <p className="text-sm text-slate-500 mb-5">Riders are a tiny fraction of road traffic but a quarter of those killed.</p>
              <div className="space-y-4">
                <Bar label="Share of road traffic" value={1} color="#94A3B8" />
                <Bar label="Share of road deaths" value={25} color="#DC2626" />
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="stat-chart-ksi">
              <h3 className="font-bold text-[#0F2744] mb-1">How serious rider injuries are</h3>
              <p className="text-sm text-slate-500 mb-5">Share of casualties that are killed or seriously injured (KSI).</p>
              <div className="space-y-4">
                <Bar label="Motorcyclists" value={39} color="#F59E0B" />
                <Bar label="Car occupants" value={17} color="#2563EB" />
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6 md:col-span-2" data-testid="stat-chart-factors">
              <h3 className="font-bold text-[#0F2744] mb-1">What's behind the most serious crashes</h3>
              <p className="text-sm text-slate-500 mb-5">Recorded factors in rider KSI collisions — exactly what advanced roadcraft targets.</p>
              <div className="grid sm:grid-cols-3 gap-x-8 gap-y-4">
                <Bar label="Ineffective observation" value={53} color="#DC2626" />
                <Bar label="Excess / inappropriate speed" value={47} color="#F59E0B" />
                <Bar label="Fatalities on rural roads" value={70} color="#0F2744" />
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-100 p-6 flex items-start gap-4" data-testid="stat-training-callout">
            <ShieldAlert className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed">
              Observation, hazard perception and speed management are the very skills advanced RoSPA training develops through the system of motorcycle control (IPSGA). Post-test coaching is one of the most effective ways for an experienced rider to cut their risk — and enjoy the road more.
            </p>
          </div>
          <p className="mt-3 text-xs text-slate-400">Sources: Reported Road Casualties Great Britain 2025 (Department for Transport) and RoSPA motorcycle safety guidance. Figures are rounded summaries for general guidance.</p>
        </section>

        {/* Roadcraft */}
        <section id="roadcraft" className="scroll-mt-24" data-testid="resource-roadcraft">
          <div className="rounded-3xl bg-[#0F2744] text-white overflow-hidden">
            <div className="p-8 sm:p-12">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-12 w-12 rounded-2xl bg-amber-500 flex items-center justify-center">
                  <BookOpen className="h-7 w-7 text-[#0F2744]" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold font-heading">{t("resources.roadcraft.title", "Roadcraft — the Police Rider's & Driver's Handbook")}</h2>
              </div>
              <div className="space-y-4 text-slate-200 leading-relaxed max-w-3xl">
                <p>{t("resources.roadcraft.p1", "Roadcraft is the official police handbook for advanced riding and driving, and it is the foundation on which RoSPA advanced training and the RoSPA test are based. It distils decades of police experience into a clear, repeatable approach to using the road safely and making good progress.")}</p>
                <p>{t("resources.roadcraft.p2", "At its core is the System of Vehicle Control (IPSGA) — a disciplined method for approaching hazards — supported by advanced observation, anticipation, positioning and hazard management. Everything your tutor coaches you on can be traced back to the principles set out in Roadcraft.")}</p>
                <p>{t("resources.roadcraft.p3", "Because the RoSPA assessment is built around these principles, reading Roadcraft (there are separate motorcycle and car editions) is one of the best ways to prepare. Your tutor will help you turn the theory in the book into confident, safe habits on the road.")}</p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold">
                  <Link to="/join" data-testid="resources-join-btn">Start advanced training <ArrowRight className="h-4 w-4 ml-2" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <Link to="/certification" data-testid="resources-cert-btn">About the RoSPA test</Link>
                </Button>
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-slate-400 text-center">
            {t("resources.roadcraft.note", "These summaries are a friendly introduction. Exact wording and emphasis are confirmed by your tutor and the current edition of Roadcraft.")}
          </p>
        </section>
      </div>
    </div>
  );
}
